"use client"

import { Navigate, Outlet, useLocation } from "react-router-dom"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { useFinanceSetupQuery } from "@/features/finance/services/finance-setup-api"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

/** The guided onboarding route. A branch that has not activated its finances is sent here. */
const CONFIGURE_PATH = "/finance/configure"

/**
 * Sends a branch that has not opened its books to the guided Configure Finance flow.
 *
 * The test is the `financeActivatedAt` FLAG — the deliberate click at the end of the wizard — not a derived
 * count of how much has been configured. A branch with an account but no fund allocation used to pass, and
 * every finance page it then reached was a page where nothing could be spent. Until the flag is set, every
 * other finance page (transactions, transfers, opening balances, reconciliation, reports) redirects here.
 *
 * This is a COURTESY, not the enforcement. The gate is in the API, at the ledger: every posting path refuses a
 * branch with no accounting identity, and money can't be recorded with nowhere to record it. What this does is
 * stop a treasurer walking into a finance screen that will only reject everything they do.
 *
 * It deliberately does NOT block while the status is loading — the ledger is the boundary, not this component.
 */
/**
 * Should this reader be sent to the guided flow?
 *
 * Named and exported so the decision can be tested against a real cache rather than only by rendering: this
 * predicate is what fired the moment a completed wizard navigated to /finance, and reading a status from
 * before the activation is what bounced the treasurer back onto the wizard they had just finished.
 *
 * Undefined means unknown (still loading, or no branch resolved) and lets them through — the ledger is the
 * boundary, not this component.
 */
export const needsFinanceSetup = (status: { isActivated: boolean } | undefined): boolean =>
  status ? !status.isActivated : false

export function FinanceSetupGuard() {
  const location = useLocation()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const { workspaceId, scopeBranch } = useFinancePageState()

  const setupQuery = useFinanceSetupQuery(workspaceId ?? null, scopeBranch?.id ?? null)

  const configureHref = buildBranchAdminUrl(branchSlug, CONFIGURE_PATH, workspaceSlug ?? undefined)
  const alreadyOnConfigure = location.pathname === configureHref

  const needsSetup = needsFinanceSetup(setupQuery.data)

  if (needsSetup && !alreadyOnConfigure) {
    return <Navigate to={configureHref} replace />
  }

  return <Outlet />
}
